import React, { useState, useEffect } from 'react';
import { 
  CalendarIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  WifiIcon
} from '@heroicons/react/24/outline';
import { 
  getCurrentYearSchedules,
  formatDday, 
  formatDate, 
  getScheduleDataSource,
  getLoadStatus,
  forceReloadScheduleData
} from '../utils/examScheduleUtils';

/**
 * 시험일정 카드 컴포넌트
 * Q-net에서 스크래핑한 시험일정 데이터를 표시합니다.
 */
const ExamScheduleCard = ({ examCode, className = '' }) => {

  // 배치 데이터에서 당해년 스케줄 정보 조회
  const [schedules, setSchedules] = useState([]);
  const [dataSource, setDataSource] = useState(null);
  const [loadStatus, setLoadStatus] = useState({ status: 'loading' });
  const [isReloading, setIsReloading] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const loadScheduleData = async () => {
      try {
        // 로딩 상태 업데이트
        const currentStatus = getLoadStatus();
        setLoadStatus(currentStatus);
        
        const scheduleData = await getCurrentYearSchedules(examCode);
        const sourceData = await getScheduleDataSource();
        
        setSchedules(scheduleData);
        setDataSource(sourceData);
        
        // 최종 로드 상태 업데이트
        const finalStatus = getLoadStatus();
        setLoadStatus(finalStatus);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('스케줄 데이터 로드 실패:', error);
        }
        setSchedules([]);
        setDataSource({
          source: 'Q-Net API (배치 시스템)',
          lastUpdated: new Date().toISOString().split('T')[0]
        });
        setLoadStatus({
          status: 'error',
          error: error.message,
          troubleshooting: [
            '브라우저 캐시를 클리어해주세요',
            '네트워크 연결을 확인해주세요',
            '페이지를 새로고침해주세요'
          ]
        });
      }
    };

    if (examCode) {
      loadScheduleData();
    }
  }, [examCode]);

  // 강제 재로드 핸들러
  const handleReload = async () => {
    setIsReloading(true);
    try {
      await forceReloadScheduleData();
      
      // 데이터 다시 로드
      const scheduleData = await getCurrentYearSchedules(examCode);
      const sourceData = await getScheduleDataSource();
      
      setSchedules(scheduleData);
      setDataSource(sourceData);
      
      const finalStatus = getLoadStatus();
      setLoadStatus(finalStatus);
    } catch (error) {
      console.error('재로드 실패:', error);
      setLoadStatus({
        status: 'error',
        error: '재로드 실패: ' + error.message,
        troubleshooting: [
          '네트워크 연결을 확인해주세요',
          '잠시 후 다시 시도해주세요',
          '다른 브라우저로 시도해보세요'
        ]
      });
    } finally {
      setIsReloading(false);
    }
  };

  // 로딩 중일 때
  if (loadStatus.status === 'loading') {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-100 ${className}`}>
        <div className="flex items-center mb-4">
          <CalendarIcon className="w-6 h-6 text-blue-500 mr-3 animate-pulse" />
          <h3 className="text-xl font-bold text-gray-900">{currentYear}년 시험일정</h3>
        </div>
        
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
          <p className="text-blue-600 mb-2">시험일정 로딩 중...</p>
          <p className="text-sm text-gray-400">잠시만 기다려주세요</p>
        </div>
      </div>
    );
  }

  // 에러 상태일 때  
  if (loadStatus.status === 'error') {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-red-100 ${className}`}>
        <div className="flex items-center mb-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">{currentYear}년 시험일정</h3>
        </div>
        
        <div className="text-center py-8">
          <WifiIcon className="w-12 h-12 text-red-300 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">데이터 로드 실패</p>
          <p className="text-sm text-gray-500 mb-4">
            {loadStatus.error || '시험일정을 불러올 수 없습니다'}
          </p>
          
          {/* 재시도 버튼 */}
          <button
            onClick={handleReload}
            disabled={isReloading}
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg font-medium text-sm transition-colors duration-200 mb-4"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${isReloading ? 'animate-spin' : ''}`} />
            {isReloading ? '재시도 중...' : '다시 시도'}
          </button>

          {/* 문제 해결 가이드 */}
          {loadStatus.troubleshooting && (
            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-red-800 mb-2">해결 방법:</p>
              <ul className="text-sm text-red-700 space-y-1 text-left">
                {loadStatus.troubleshooting.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-400 mr-2">•</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 백업 데이터 소스 표시 */}
          {loadStatus.dataSource && (
            <div className="bg-yellow-50 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">
                <strong>데이터 소스:</strong> {loadStatus.dataSource}
                {loadStatus.cacheAge && ` (${loadStatus.cacheAge}시간 전 캐시)`}
              </p>
            </div>
          )}
          
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
          >
            Q-net 공식 홈페이지에서 확인하기 →
          </a>
        </div>
      </div>
    );
  }

  // 빈 데이터일 때(정상 로딩되었지만 해당 연도 일정이 없는 경우) - 공식 안내로 깔끔히 대체
  if (!schedules || schedules.length === 0) {
    return (
      <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 ${className}`}>
        <div className="flex items-center mb-4">
          <CalendarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">시험일정</h3>
        </div>

        <div className="text-center py-6">
          <CalendarIcon className="w-12 h-12 text-orange-200 mx-auto mb-4" />
          <p className="text-gray-700 mb-1 font-medium">정확한 시험일정은 큐넷에서 확인하세요</p>
          <p className="text-sm text-gray-400 mb-5">
            원서접수·시험일은 매년 변경되므로 공식 일정을 확인하는 것이 가장 정확합니다.
          </p>
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition-colors"
          >
            큐넷 공식 시험일정 보기 →
          </a>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'text-green-600 bg-green-50 border-green-200';
      case 'upcoming': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'upcoming':
        return <ClockIcon className="w-4 h-4" />;
      case 'closed':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  // 일정 아이템 렌더링 함수 (중복 코드 제거)
  const renderScheduleItem = (schedule, isNext, isPast) => (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          <span className={`px-2 py-1 rounded text-xs font-semibold ${
            isNext ? 'bg-orange-200 text-orange-800' : 
            isPast ? 'bg-gray-200 text-gray-600' : 
            'bg-blue-200 text-blue-800'
          }`}>
            {schedule.round}
          </span>
          
          {!isPast && (
            <span className={`text-xs font-medium ${
              schedule.dday <= 30 ? 'text-red-600' : 
              schedule.dday <= 60 ? 'text-yellow-600' : 
              'text-green-600'
            }`}>
              {formatDday(schedule.dday)}
            </span>
          )}
        </div>
        
        <div className="text-sm font-medium text-gray-900 mb-1">
          시험일: {formatDate(schedule.examDateObj)}
        </div>
        
        <div className="text-xs text-gray-600 space-y-1">
          {schedule.applicationPeriod && (
            <div>접수: {schedule.applicationPeriod}</div>
          )}
          {schedule.examType && (
            <div>시험유형: {schedule.examType}</div>
          )}
          {schedule.resultDate && (
            <div>발표일: {schedule.resultDate}</div>
          )}
        </div>
      </div>

      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(schedule.applicationStatus.status)}`}>
        {getStatusIcon(schedule.applicationStatus.status)}
        <span className="ml-1 hidden sm:inline">{schedule.applicationStatus.message}</span>
      </div>
    </div>
  );

  // 가장 가까운 미래 일정과 과거 일정 분류
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const futureSchedules = schedules.filter(s => s.isFuture);
  const nextSchedule = futureSchedules.length > 0 ? futureSchedules[0] : null;

  // 다중 분야 자격증인지 확인 (분야 정보가 있는 일정이 있는 경우)
  const hasMultipleFields = schedules.some(s => s.examField);
  
  // 분야별로 그룹화 (다중 분야인 경우)
  const schedulesByField = hasMultipleFields ? 
    schedules.reduce((acc, schedule) => {
      const field = schedule.examField || '기본';
      if (!acc[field]) acc[field] = [];
      acc[field].push(schedule);
      return acc;
    }, {}) : null;

  return (
    <div className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <CalendarIcon className="w-6 h-6 text-orange-500 mr-3" />
          <h3 className="text-xl font-bold text-gray-900">{currentYear}년 시험일정</h3>
        </div>
        
        <div className="text-sm text-gray-500">
          총 {schedules.length}회 시행
        </div>
      </div>

      {/* 다음 시험일정 하이라이트 */}
      {nextSchedule && (
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">다가오는 시험</h4>
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
              nextSchedule.dday <= 30 ? 'text-red-600 bg-red-100' : 
              nextSchedule.dday <= 60 ? 'text-yellow-600 bg-yellow-100' : 
              'text-green-600 bg-green-100'
            }`}>
              {formatDday(nextSchedule.dday)}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 시험 정보 */}
            <div>
              <div className="text-sm text-orange-600 font-medium mb-2">{nextSchedule.round}</div>
              <div className="text-2xl font-bold text-gray-900 mb-2">
                {formatDate(nextSchedule.examDateObj)}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                {nextSchedule.examField && (
                  <div className="text-blue-600 font-medium">
                    분야: {nextSchedule.examField}
                  </div>
                )}
                {nextSchedule.examType && (
                  <div>시험유형: {nextSchedule.examType}</div>
                )}
              </div>
            </div>

            {/* 접수 정보 */}
            <div>
              <div className="text-sm text-gray-600 font-medium mb-2">접수 현황</div>
              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(nextSchedule.applicationStatus.status)}`}>
                {getStatusIcon(nextSchedule.applicationStatus.status)}
                <span className="ml-2">{nextSchedule.applicationStatus.message}</span>
              </div>
              
              {nextSchedule.applicationPeriod && (
                <div className="mt-3 text-sm text-gray-600">
                  접수기간: {nextSchedule.applicationPeriod}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 전체 시험일정 목록 */}
      <div className="space-y-4 mb-6">
        <h4 className="text-lg font-bold text-gray-900">
          {currentYear}년 전체 일정 ({schedules.length}회)
          {hasMultipleFields && (
            <span className="text-sm font-normal text-blue-600 ml-2">
              · {Object.keys(schedulesByField).length}개 분야
            </span>
          )}
        </h4>
        
        {hasMultipleFields ? (
          // 분야별 그룹화된 표시
          <div className="space-y-6">
            {Object.entries(schedulesByField).map(([field, fieldSchedules]) => (
              <div key={field} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                <h5 className="text-md font-semibold text-blue-800 mb-3">
                  {field} 분야 ({fieldSchedules.length}회)
                </h5>
                <div className="space-y-3">
                  {fieldSchedules.map((schedule, index) => {
                    const isPast = schedule.isPast;
                    const isNext = nextSchedule && schedule.round === nextSchedule.round;
                    
                    return (
                      <div 
                        key={`${field}-${index}`} 
                        className={`border rounded-lg p-4 transition-all duration-200 ${
                          isNext ? 'border-orange-300 bg-orange-50' : 
                          isPast ? 'border-gray-200 bg-gray-50' : 
                          'border-blue-200 bg-white'
                        }`}
                      >
                        {renderScheduleItem(schedule, isNext, isPast)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // 기본 단일 목록 표시
          <div className="space-y-3">
            {schedules.map((schedule, index) => {
              const isPast = schedule.isPast;
              const isNext = nextSchedule && schedule.round === nextSchedule.round;
              
              return (
                <div 
                  key={index} 
                  className={`border rounded-lg p-4 transition-all duration-200 ${
                    isNext ? 'border-orange-300 bg-orange-50' : 
                    isPast ? 'border-gray-200 bg-gray-50' : 
                    'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          isNext ? 'bg-orange-200 text-orange-800' : 
                          isPast ? 'bg-gray-200 text-gray-600' : 
                          'bg-blue-200 text-blue-800'
                        }`}>
                          {schedule.round}
                        </span>
                        
                        {!isPast && (
                          <span className={`text-xs font-medium ${
                            schedule.dday <= 30 ? 'text-red-600' : 
                            schedule.dday <= 60 ? 'text-yellow-600' : 
                            'text-green-600'
                          }`}>
                            {formatDday(schedule.dday)}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        시험일: {formatDate(schedule.examDateObj)}
                      </div>
                      
                      <div className="text-xs text-gray-600 space-y-1">
                        {schedule.examField && (
                          <div className="text-blue-600 font-medium text-xs">
                            분야: {schedule.examField}
                          </div>
                        )}
                        {schedule.applicationPeriod && (
                          <div>접수: {schedule.applicationPeriod}</div>
                        )}
                        {schedule.examType && (
                          <div>유형: {schedule.examType}</div>
                        )}
                        {schedule.resultDate && (
                          <div>발표: {schedule.resultDate}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(schedule.applicationStatus.status)}`}>
                      {schedule.applicationStatus.status === 'open' ? '접수중' :
                       schedule.applicationStatus.status === 'upcoming' ? '접수예정' :
                       isPast ? '시험완료' : '접수마감'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 추가 정보 및 액션 */}
        <div className="space-y-4">
        {/* 접수 상태별 액션 버튼 - 다음 시험 기준 */}
        {nextSchedule && (
          <>
            {nextSchedule.applicationStatus.status === 'open' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-green-800">
                      {nextSchedule.round} 접수가 진행중입니다!
                    </div>
                    <div className="text-sm text-green-600">
                      {nextSchedule.applicationStatus.daysRemaining}일 남음
                    </div>
                  </div>
                  <a
                    href="https://www.q-net.or.kr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200"
                  >
                    Q-net 접수
                  </a>
                </div>
              </div>
            )}

            {nextSchedule.applicationStatus.status === 'upcoming' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="font-semibold text-blue-800">
                  {nextSchedule.round} 접수 예정
                </div>
                <div className="text-sm text-blue-600">
                  {nextSchedule.applicationStatus.daysRemaining}일 후 접수 시작
                </div>
              </div>
            )}

            {nextSchedule.applicationStatus.status === 'closed' && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="font-semibold text-gray-700">
                  {nextSchedule.round} 접수 마감
                </div>
                <div className="text-sm text-gray-600">
                  다음 회차 일정을 확인해보세요
                </div>
              </div>
            )}

            {/* 학습 가이드 - 다음 시험 기준 */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <ClockIcon className="w-4 h-4 text-orange-600 mr-2" />
                <span className="font-semibold text-orange-800">학습 가이드</span>
              </div>
              <div className="text-sm text-orange-700">
                {nextSchedule.dday > 90 ? '기초 이론 학습 단계 - 충분한 시간이 있습니다!' :
                 nextSchedule.dday > 60 ? '심화 학습 및 문제풀이 단계' :
                 nextSchedule.dday > 30 ? '집중 문제풀이 및 약점 보완 단계' :
                 nextSchedule.dday > 14 ? '최종 점검 및 모의고사 단계' :
                 '마지막 정리 및 컨디션 관리 단계'}
              </div>
            </div>
          </>
        )}

        {/* 다음 시험이 없을 때 */}
        {!nextSchedule && schedules.length > 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="font-semibold text-gray-700">
              {currentYear}년 모든 시험이 종료되었습니다
            </div>
            <div className="text-sm text-gray-600">
              {currentYear + 1}년 일정을 Q-net에서 확인해보세요
            </div>
          </div>
        )}

        {/* Q-net 링크 */}
        <div className="text-center">
          <a
            href="https://www.q-net.or.kr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-orange-600 hover:text-orange-700 text-sm font-medium transition-colors"
          >
            Q-net 공식 홈페이지에서 자세한 정보 확인하기 →
          </a>
        </div>
        </div>

        {/* 데이터 소스 정보 */}
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>출처: {dataSource?.source || 'Q-Net API'}</span>
            <span>업데이트: {dataSource?.lastUpdated || '로딩 중...'}</span>
          </div>
          
          {/* 로드 상태 추가 정보 */}
          {loadStatus.dataSource && loadStatus.dataSource !== 'primary' && (
            <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
              <div className="flex items-center text-yellow-700">
                <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                <span className="font-medium">
                  {loadStatus.dataSource === 'localStorage' && '로컬 백업 데이터 사용 중'}
                  {loadStatus.dataSource === 'fallback' && '정적 폴백 데이터 사용 중'}
                </span>
              </div>
              {loadStatus.cacheAge && (
                <div className="text-yellow-600 mt-1">
                  캐시된 데이터: {loadStatus.cacheAge}시간 전
                </div>
              )}
            </div>
          )}
          
          {/* 성공적인 1차 로드일 때 표시 */}
          {loadStatus.dataSource === 'primary' && loadStatus.scheduleCount > 0 && (
            <div className="mt-2 text-xs text-green-600">
              ✓ 최신 데이터 ({loadStatus.scheduleCount}개 자격증)
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamScheduleCard;